import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Alert from 'components/atoms/Alert/Alert';
import LoadingOverlay from 'components/atoms/LoadingOverlay/LoadingOverlay';
import { ProgressBar } from 'components/atoms/ProgressBar/ProgressBar';
import Txt from 'components/atoms/Txt/Txt';
import { RegistrationData } from 'components/templates/AuthenticationProvider/authApiUtils';
import { useSignUp } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import { SecondFactorRequiredError } from 'components/templates/AuthenticationProvider/SecondFactorRequiredError';
import { LoginError } from 'components/templates/AuthenticationProvider/Types';
import { useCountdownTimer } from 'hooks/useCountdownTimer';
import usePromiseState from 'hooks/usePromiseState';
import useRecaptcha, { RecaptchaProvider } from 'hooks/useRecaptcha';
import useUserSourceData from 'hooks/useUserSourceData';
import { useRouter } from 'next/router';
import React, { Reducer, useCallback, useEffect, useReducer, useState } from 'react';
import { isValidEmail } from 'utils/formHelper';
import { BasicDetailsStep } from './BasicDetailsStep';
import classes from './RegistrationForm.mobiworkx.module.scss';
import { UsernamePasswordStep } from './UsernamePasswordStep';

export type Props = {};

export type FormData = {
  username: string;
  uid: string;
  provider: string;
  socialToken: string;
  firstName: string;
  lastName: string;
  password: string;
  sex: string;
  gender: string;
  dateOfBirth: Date;
  address: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    postcode: string;
  };
  social: {
    name: string;
    firstName: string;
    lastName: string;
  };
  terms: boolean;
  termsVersion: string;
  secondFactorCode?: string;
  sessionId?: string;
  overrideCountry?: string;
  turnstileToken?: string;
};

const STEPS: React.FC<StepProps>[] = [BasicDetailsStep, UsernamePasswordStep];

function RegistrationForm(_props: Props) {
  const router = useRouter();
  const [step, setStep] = useState<number>(1);
  const [availableSteps, setAvailableSteps] = useState<React.FC<StepProps>[]>(STEPS);
  const [error, setError] = useState<LoginError | null>(null);
  const [formData, dispatchFormData] = useReducer<
    Reducer<Partial<FormData>, Partial<FormData>>,
    Partial<FormData>
  >(
    (prevState, action) => {
      return Object.assign({}, prevState, action);
    },
    {},
    () => ({}),
  );

  const [signUp, { isLoading }] = usePromiseState(useSignUp());

  const { loadRecaptcha, isLoadingRecaptcha } = useRecaptcha('registration');

  const { startCountdown } = useCountdownTimer('secondFactorCountdown');

  const handleNextStep = async (data: Partial<FormData>) => {
    window.scrollTo(0, 0);
    dispatchFormData(data);
    if (step < availableSteps.length) {
      setStep((step) => step + 1);
    } else {
      const recaptchaData = await loadRecaptcha();
      const completeData = Object.assign({}, formData, data) as RegistrationData;
      try {
        await signUp(completeData, recaptchaData);
      } catch (err: any) {
        if (err instanceof SecondFactorRequiredError) {
          startCountdown();
          router.push(
            {
              pathname: '/requires-confirmation',
              query: {
                sessionId: err.getSessionId(),
                username: completeData.username,
                ...router.query,
              },
            },
            '/requires-confirmation',
          );
        }
        // A 'LoginError' may not be an error in the traditional sense, but rather a notification that the user must complete the DOI process:
        else if (
          err instanceof LoginError &&
          err.payload &&
          err.payload.requiresEmailConfirmation
        ) {
          await router.push(
            {
              pathname: '/requires-email-confirmation',
              query: { email: completeData.username, ...router.query },
            },
            '/requires-email-confirmation',
          );
        } else {
          setError(err);
        }
      }
    }
  };

  const handlePreviousStep = useCallback(() => {
    setStep((step) => step - 1);
  }, []);

  const CurrentStep: React.FC<StepProps> = availableSteps[step - 1];

  const { contactEmail, socialToken } = useUserSourceData();
  const { isReady, pathname, query } = router;
  // JoinApi uses the contactEmail as does KPP social flow, so check for absense of socialToken.
  const isJoinApi = contactEmail && isValidEmail(contactEmail) && !socialToken;
  useEffect(() => {
    if (isJoinApi) {
      dispatchFormData({
        username: contactEmail,
      });
      // Skip InitalEmailStep if valid email
      setAvailableSteps(availableSteps.slice(availableSteps.indexOf(UsernamePasswordStep) + 1));
    } else if (isReady) {
      const {
        provider,
        uid,
        email,
        contactEmail,
        name,
        first_name,
        firstName,
        last_name,
        lastName,
        verified,
        socialToken,
      } = query;
      if (email && isValidEmail(email as string)) {
        // JADE-756 Social registration - OneP-facilitated solution with query params
        if (provider && uid && Boolean(verified)) {
          dispatchFormData({
            provider: provider,
            uid: uid,
            email: email,
            firstName: first_name || firstName,
            lastName: last_name || lastName,
            social: {
              name: name,
              firstName: first_name || firstName,
              lastName: last_name || lastName,
            },
          } as Partial<FormData>);
        } else {
          // KPP-789 Miniform redirect registration - OneP miniform with query params
          dispatchFormData({
            username: email,
            firstName: first_name || firstName,
            lastName: last_name || lastName,
          } as Partial<FormData>);
        }
        // unlike JoinApi process above, social/organic reg is user-initiated with email entry
        // on the first screen, so only advance to the second screen without removing it
        setStep(availableSteps.indexOf(BasicDetailsStep) + 1);
        // remove query parameters
        router.replace({ pathname }, undefined, { shallow: true });
      } else if (contactEmail && isValidEmail(contactEmail as string) && socialToken) {
        // KPP-1379 KPP social reg flow
        dispatchFormData({
          socialToken: socialToken,
          username: contactEmail,
          firstName: first_name || firstName,
          lastName: last_name || lastName,
          social: {
            name: name,
            firstName: first_name || firstName,
            lastName: last_name || lastName,
          },
        } as Partial<FormData>);
        setStep(availableSteps.indexOf(BasicDetailsStep) + 1);
        // remove query parameters
        router.replace({ pathname }, undefined, { shallow: true });
      }
    }
  }, [contactEmail, socialToken, isReady, isJoinApi, availableSteps, query, router, pathname]);

  return (
    <>
      {(isLoading || isLoadingRecaptcha || !isReady) && <LoadingOverlay />}
      <CurrentStep
        onNext={handleNextStep}
        progressIndicator={
          <div className={clsx(classes.progressBar)}>
            <ProgressBar style={'linear'} max={availableSteps.length - 1} value={step - 1} />
            <Txt component={'span'}>
              <Txt component={'b'} skipTranslation>
                {{ step: step - 1 }}
              </Txt>{' '}
              of {{ total: availableSteps.length - 1 }}
            </Txt>
          </div>
        }
        error={
          error ? (
            <Alert
              type={'error'}
              title={'An error occurred'}
              subtitle={error.message}
              onClose={() => setError(null)}
            />
          ) : undefined
        }
        onPrevious={handlePreviousStep}
        currentFormData={formData}
        menu={
          <p>
            <Txt className={classes.alreadyAMember}>Already a LifePoints member?</Txt>
            <A className={classes.signUpLink} href={'/login'}>
              <Txt>sign in</Txt>
            </A>
          </p>
        }
      />
    </>
  );
}

export type StepProps = {
  onNext?: (newData: Partial<FormData>) => void;
  onPrevious?: () => void;
  currentFormData: Partial<FormData>;
  progressIndicator?: React.ReactNode;
  menu?: React.ReactNode;
  error: React.ReactNode | undefined;
  sessionId?: string;
};

export default function RegistrationFormWrapper(props: Props) {
  return (
    <RecaptchaProvider>
      <RegistrationForm {...props} />
    </RecaptchaProvider>
  );
}
